import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay';
import ScoreListItem from './components/ScoreListItem';
import ProgressBar from './components/ProgressBar';
import './GutLanding.scss';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchHealthSpanProfileAction } from '../../actions/healthSpanActions';
import ConfirmButton from './components/ConfirmButton';
import { useScoreCategoryMetadata } from './useScoreCategoryMetadata';
import {
  HEALTH_SPAN_MAX_SCORE,
  SUB_CATEGORY_KEY_MAP,
  buildLandingScores,
  hasValidScore,
  getSubCategoryDisplayProps,
} from './healthSpanScore.utils';

const ASSESSMENT_IDS = {
  gutBalance: process.env.REACT_APP_GUT_ASSESSMENT_ID || '',
};

const CATEGORIES = [
  {
    key: 'gutBalance',
    image: assessmentImage.gutImage,
    name: 'สมดุลลำไส้',
  },
];

const GutLanding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.healthSpan);
  const { metadata, metaLoading } = useScoreCategoryMetadata('gut');

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  const { categoryScore, categoryStatus, scores } = buildLandingScores(
    profile,
    'gut',
    CATEGORIES,
    metadata
  );

  const hasValue = hasValidScore(categoryScore);
  const isLoading = loading || metaLoading;
  const tip = categoryStatus?.tip;
  const noDataColor = '#A1A1A1';
  const displayColor = hasValue ? categoryStatus.barColor : noDataColor;

  const handleCategoryClick = (category) => {
    const assessmentId = ASSESSMENT_IDS[category.key];
    if (!assessmentId) return;

    history.push(
      `${commonConstant.pathAssessmentQuestion}?assessment_id=${assessmentId}&return=${commonConstant.pathGutBalanceAssessmentLanding}&section=${category.key}`
    );
  };

  const handleAddUpdateClick = () => {
    const gutId = process.env.REACT_APP_GUT_ASSESSMENT_ID || '';
    history.push(`${commonConstant.pathGutAssessmentLanding}?id=${gutId}&return=${encodeURIComponent(commonConstant.pathGutBalanceAssessmentLanding)}`);
  };

  const headerScore = hasValue ? categoryScore : '-';
  const headerMaxScore = categoryStatus?.maxScore || HEALTH_SPAN_MAX_SCORE;

  return (
    <div className="gut-landing-component">
      {isLoading && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {!isLoading && (
        <>
          <div className="bg-detail-container">
            <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
          </div>

          <div className="content-container">
            <div className="header-section">
              <div className="image-component">
                <img src={assessmentImage.gutImage} alt="" />
              </div>
              <div className="detail" style={{ color: displayColor }}>
                <div className="header-name">{metadata?.display_name || 'สมดุลลำไส้'}</div>
                <div className="status">{hasValue ? categoryStatus.statusText : 'ไม่มีข้อมูล'}</div>
                <div className="score-container">
                  <div className="value">{headerScore}</div>
                  <div className="max-value">/{headerMaxScore}</div>
                </div>
                <ProgressBar
                  value={hasValue ? categoryScore : 0}
                  maxValue={headerMaxScore}
                  width="130px"
                  barColor={displayColor}
                />
              </div>
            </div>

            {hasValue && tip && (
              <div className="tip-card">
                <div className="tip-label-container">
                  <img src={assessmentImage.tipIcon} alt="" className="tip-label-image" />
                  <div className="tip-label-text">คำแนะนำ</div>
                </div>
                <div className="tip-detail-container">
                  <img src={tip.image || assessmentImage.tipGutImage || assessmentImage.vetgetableImage} alt="" className="image" />
                  <div className="text-container">
                    <div className="tip-text">{tip.title}</div>
                    <div className="tip-detail-text">{tip.description}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="summary-score-list">
              <div className="title">ข้อมูลสมดุลลำไส้</div>

              {CATEGORIES.map((category) => {
                const scoreData = scores[category.key] || {};
                const hasData = hasValidScore(scoreData.score);
                const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
                const subName =
                  metadata?.sub_categories?.[apiKey]?.display_name || category.name;

                // Use raw gut assessment score from details instead of normalized score
                const gutDetail = profile?.scoreDetails?.gut?.details;
                const rawGutScore = gutDetail?.gut?.score;
                const displayScore = rawGutScore !== undefined && rawGutScore !== null
                  ? Math.round(rawGutScore)
                  : (hasData ? scoreData.score : '-');
                const hasDisplayData = rawGutScore !== undefined && rawGutScore !== null
                  ? hasValidScore(rawGutScore)
                  : hasData;
                const subMaxScore = metadata?.sub_categories?.[apiKey]?.max_score
                  || scoreData.maxScore || HEALTH_SPAN_MAX_SCORE;
                const display = getSubCategoryDisplayProps(scoreData, hasDisplayData);

                return (
                  <ScoreListItem
                    key={category.key}
                    image={category.image}
                    name={subName}
                    statusText={display.statusText}
                    statusColor={display.barColor}
                    value={hasDisplayData ? displayScore : '-'}
                    maxValue={subMaxScore}
                    barColor={display.barColor}
                    valueColor={display.valueColor}
                    hasData={hasDisplayData}
                    clickable={false}
                  />
                );
              })}
            </div>

            <div className="description-card">
              <div className="title">สุขภาพลำไส้ (Gut Health)</div>
              <div className="description-text">
                <span className="highlight">“ลำไส้ของคุณกำลังบอกอะไร?”</span>
                ทำแบบประเมิน Gut Health เพื่อสำรวจสุขภาพลำไส้ใน 5 ด้าน และค้นพบแนวทางดูแลตัวเองที่เหมาะกับคุณ
              </div>
            </div>
          </div>
        </>
      )}

      <div className="footer-container">
        <ConfirmButton text={hasValue ? 'อัปเดทข้อมูล' : 'เพิ่มข้อมูล'} width={'100%'} color={'black'} onClick={handleAddUpdateClick} />
      </div>
    </div>
  );
};

export default GutLanding;
